import { useMutation } from "@tanstack/react-query";
import { isApiError } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import type { ModelId } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useUiStore } from "@/stores/uiStore";
import { ModelSheet } from "@/features/chat/sheets/ModelSheet";

type Props = { open: boolean; onClose: () => void };

/** 1.18 기본 대화 모델(새 대화 기본). ModelSheet 재사용 → PATCH /me/settings {defaultModel} → authStore.settings 갱신. */
export function DefaultModelSheet({ open, onClose }: Props) {
  const models = useConfigStore((s) => s.settings?.pricing.models ?? []);
  const current = useAuthStore((s) => s.settings?.defaultModel);
  const showSnackbar = useUiStore((s) => s.showSnackbar);

  const patch = useMutation({
    mutationFn: (defaultModel: ModelId) => call(api.PATCH("/me/settings", { params: { header: xClient() }, body: { defaultModel } })),
    onSuccess: (res) => {
      useAuthStore.setState({ settings: res.settings });
      showSnackbar(t("settings.defaultModel.saved"));
      onClose();
    },
    onError: (e) => {
      if (isApiError(e) && e.code === "MODEL_UNAVAILABLE") showSnackbar(t("turn.modelUnavailable"));
      else showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("chat.settings.failed"));
    },
  });

  const resolvedCurrent = models.find((m) => m.id === current)?.id ?? models.find((m) => m.isDefault)?.id;

  return (
    <ModelSheet
      open={open}
      title={t("settings.defaultModel")}
      models={models}
      current={resolvedCurrent}
      applying={patch.isPending}
      onApply={(m) => {
        if (m === resolvedCurrent) {
          onClose();
          return;
        }
        patch.mutate(m);
      }}
      onClose={onClose}
    />
  );
}
